import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useClubRadar } from "../../hooks/club-radar/useClubRadar";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { canUseClubRadar } from "../../utils/clubRoles";
import ClubListsSidebar from "../../components/club-radar/ClubListsSidebar";
import ClubListHeader from "../../components/club-radar/ClubListHeader";
import ClubFilterChips from "../../components/club-radar/ClubFilterChips";
import ClubEntryTable, { ClubPlayerCell } from "../../components/club-radar/ClubEntryTable";
import ClubEntryDetail from "../../components/club-radar/ClubEntryDetail";
import ClubDeleteListModal from "../../components/club-radar/ClubDeleteListModal";
import ClubManageSharingSheet from "../../components/club-radar/ClubManageSharingSheet";
import { ensureRadarAnim } from "../../components/club-radar/ClubRadarBits";
import ClubRadarSharePreview from "../../components/club-radar/ClubRadarSharePreview";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import SwipeActionRow from "../../components/common/SwipeActionRow";
import { clubRadarService } from "../../services/clubRadarService";
import { RADAR_TOAST_STYLE, playersCountText } from "../../components/agent-radar/radarFormat";
import { clubStatusMeta } from "../../constants/clubRadarChoices";
import {
  AvatarSquare,
  RadarEmptyBlock,
  SearchInput,
  DashedCtaButton,
  Eyebrow,
  SharedChip,
  ShareBadge,
  FilterChip,
  IconSquareButton,
  TextButton,
  ModalShell,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
  StatusPill,
  SourceLines,
  FONT_FAMILY,
  GLYPH,
} from "../../components/club-radar/v2";
import {
  MobileNewListSheet,
  MobileSortSheet,
  MobileEntryActionsSheet,
  MobileListMenuSheet,
} from "../../components/club-radar/ClubRadarMobileSheets";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// TP Club Radar — the club's shared watchlist (P3), V2 UI rebuild 1:1 from the
// Club Radar artboards (frames 1a/1b/1c desktop · 1d/1e/1f mobile · 1p/1q
// delete confirms). Pure lists (R1): screen top bar + lists sidebar + selected-
// list header (badge / description / ⋯) + status filter chips + entries table
// on desktop; a lists home + list detail on mobile. Add-to-Radar lives ONLY in
// the player profile header (R10, P4) — this page has no add button (frames
// 1c/1f), the empty state points to the profile. PRESENTATION ONLY: all
// handlers, endpoints, gates and toasts below are the pre-rebuild ones.
const radarToast = (msg) => toast(msg, { position: "bottom-center", style: RADAR_TOAST_STYLE });

// A truly empty list is not a filter miss. The agent radar already draws that
// distinction (AgentRadar.js:213-233); the club side showed the filter-mismatch
// line on a brand-new list with no filter set, which reads like something was
// hidden. No copy spec exists for this case, so the agent radar's own wording is
// reused verbatim rather than a third variant invented.
const EMPTY_LIST_TITLE = "No players in this list yet";
const EMPTY_LIST_BODY = "Add players from any player profile, or move entries here with an entry's ⋯ menu.";

// Share-to-chat adapter — the platform-standard ShareActionSheet, mapped to the
// club radar share endpoint (POST lists/{id}/share/), exactly like the agent
// RadarShareAdapter. Read-only snapshot; notes/statuses never travel (R5).
const ClubRadarShareAdapter = ({ target, clubName, sharedBy, onClose }) => {
  const onShare = async (recipients, message) => {
    const channelIds = (recipients || []).map((r) => r.channel_id_candidate || r.channel_id).filter(Boolean);
    if (channelIds.length === 0) {
      toast.error("Open a chat with these contacts first, then share.");
      return { success: false };
    }
    const res = await clubRadarService.shareList(target.listId, { channelIds, message });
    if (res?.success) {
      radarToast("Shared in chat");
      return { success: true };
    }
    if (res?.status === 403) toast.error("You're not in one of these chats yet. Open it first, then share.");
    else toast.error(res?.error || "Could not share into the chat.");
    return { success: false };
  };
  // FINDING-1 (Lead decision, Option B): pass the read-only snapshot preview card
  // (design 1n/1o) into the shared sheet's additive `preview` slot.
  const preview = target ? (
    <ClubRadarSharePreview
      listName={target.name}
      count={target.count}
      players={target.players || []}
      sharedBy={sharedBy}
      clubName={clubName}
    />
  ) : null;
  return <ShareActionSheet isOpen={!!target} onClose={onClose} customShareHandler={onShare} title="Share list to chat" preview={preview} />;
};

// Empty state (frames 1c desktop / 1f mobile) — S2 radar illustration; no add
// button, the entry point stays in the player-profile header.
const RadarEmpty = ({ mobile }) => (
  <RadarEmptyBlock
    mobile={mobile}
    title="Your radar is empty"
    body={
      mobile ? (
        <>
          Open a player profile and tap{" "}
          <span style={{ fontWeight: 700, color: "#374151" }}>{GLYPH.quoteOpen}Add to Radar{GLYPH.quoteClose}</span>.
          Saved recommendations keep their recommender attribution.
        </>
      ) : (
        <>
          Open any player profile and choose{" "}
          <span style={{ fontWeight: 700, color: "#374151" }}>{GLYPH.quoteOpen}Add to Radar{GLYPH.quoteClose}</span>{" "}
          to start a watchlist. Entries saved from scout-request recommendations keep their recommender attribution.
        </>
      )
    }
  />
);

// No-access state (R9): a club user without access_radar reaching the page by URL.
const NoAccess = () => (
  <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6" style={{ fontFamily: FONT_FAMILY }}>
    <div className="text-[15px] font-bold text-[#111827]">Radar isn&rsquo;t enabled for your role</div>
    <div className="text-[12.5px] text-[#6B7280] mt-2 max-w-[340px] leading-[1.55]">A club admin can grant radar access in role management.</div>
  </div>
);

// Small desktop "new list" modal (mobile uses the bottom sheet). No artboard
// frame of its own — styled with the kit's modal primitives (1p/1n vocabulary);
// same behavior (maxLength 60, Enter submits, disabled while empty).
const NewListModal = ({ onCreate, onClose }) => {
  const [name, setName] = useState("");
  const submit = () => { const v = name.trim(); if (v) onCreate(v); };
  return (
    <ModalShell open onClose={onClose} width={430} padding={22}>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>New list</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        autoFocus
        placeholder="List name"
        maxLength={60}
        className="w-full outline-none"
        style={{ height: 38, border: "1px solid #E5E7EB", borderRadius: 10, padding: "0 12px", fontSize: 13.5, marginTop: 12, boxSizing: "border-box", fontFamily: "inherit" }}
      />
      <ModalFooter>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={submit} disabled={!name.trim()}>Create list</PrimaryButton>
      </ModalFooter>
    </ModalShell>
  );
};

// Screen top bar (§4 item 1 — frames 1a–1d/1f): "Radar" + the viewer's avatar.
const RadarTopBar = ({ mobile, user }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      padding: mobile ? "16px 16px 12px" : "15px 28px",
      background: "#FFFFFF",
      borderBottom: "1px solid #E5E7EB",
    }}
  >
    <div style={{ fontSize: mobile ? 21 : 20, fontWeight: 800, letterSpacing: "-0.3px", color: "#111827" }}>Radar</div>
    <AvatarSquare
      name={user?.name}
      imageUrl={user?.profile_picture || user?.avatar_url}
      size={mobile ? 28 : 30}
      radius={9}
      fontSize={mobile ? 10 : 11}
      style={{ marginLeft: "auto" }}
    />
  </div>
);

// Compact share-tier pill (mobile 1e header: "Shared · 3" / "Shared with club"
// / "Personal") — real counts from list state.
const MobileTierPill = ({ list }) => {
  const tier = list?.share_tier || "personal";
  if (tier === "personal") return <ShareBadge tone="neutral" pad="1px 7px" fontSize={10}>Personal</ShareBadge>;
  if (tier === "club") return <ShareBadge tone="shared" pad="1px 7px" fontSize={10}>Shared with club</ShareBadge>;
  const n = list.shared_member_count ?? (list.shared_member_preview || []).length;
  return <ShareBadge tone="shared" pad="1px 7px" fontSize={10}>{`Shared ${GLYPH.middot} ${n}`}</ShareBadge>;
};

const ClubRadar = () => {
  const navigate = useNavigate();
  // QAV5-502: window.prompt() titles are native dialogs the DOM scanner
  // cannot reach — route them through the same static-bundle lookup the
  // scanner uses before they go to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { currentRole, permissions, user } = useSelector((state) => state.auth);
  const clubName = user?.club_name || user?.club?.name || user?.user_details?.club_name || "your club";

  const hasAccess = canUseClubRadar(currentRole, permissions);

  const radar = useClubRadar();
  const {
    myLists, sharedLists, allLists, labels, entries, visibleEntries, statusCounts,
    selectedList, setSelectedList, loading, listsLoading, error,
    filter, setFilter, filterLabel, setFilterLabel, sortBy, setSortBy,
    createList, updateList, deleteList, setSharing,
    removeEntry, moveEntry, copyEntry, setStatus, assignLabel, unassignLabel, createLabel, addNote,
  } = radar;

  // Is anything actually filtered out? Mirrors AgentRadar.js:123 — the two radar
  // filter chips ("all" = off) decide filter-miss vs. genuinely-empty copy below.
  const radarFiltersActive = filter !== "all" || filterLabel !== "all";

  const [detailEntry, setDetailEntry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [manageSharing, setManageSharing] = useState(null);
  const [newListOpen, setNewListOpen] = useState(false);
  const [mobileView, setMobileView] = useState("home"); // 'home' | 'detail'
  const [mobileSheet, setMobileSheet] = useState(null); // 'newlist' | 'sort' | 'listmenu'
  const [entryActions, setEntryActions] = useState(null);
  // R5: which view the entry-actions sheet opens on — the row ⋯ opens the full
  // sheet (null), the swipe "Move"/"Copy" accelerators deep-link straight to
  // the same sheet's list picker (identical handlers, one path).
  const [entryActionsView, setEntryActionsView] = useState(null);
  // Mobile lists-home search (frame 1d) — same client-side name filter as the
  // desktop sidebar (F2), surfaced at the artboard's mobile placement.
  const [mobileListQuery, setMobileListQuery] = useState("");

  React.useEffect(() => { ensureRadarAnim(); }, []);

  const selectedMeta = allLists.find((l) => l.id === selectedList) || null;
  const canManage = !!selectedMeta?.can_manage;
  const totalCount = useMemo(() => allLists.reduce((sum, l) => sum + (l.entry_count || 0), 0), [allLists]);
  const radarEmpty = !listsLoading && totalCount === 0;

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleCreateList = async (name) => {
    setNewListOpen(false);
    setMobileSheet(null);
    const res = await createList({ name });
    if (res.success) { radarToast(`List "${name}" created`); if (isMobile) setMobileView("detail"); }
    else toast.error(res.error || "Could not create the list.");
  };
  const handleRename = async (id, name) => {
    const res = await updateList(id, { name });
    if (!res.success) toast.error(res.error || "Could not rename the list.");
  };
  const handleSaveDescription = async (id, description) => {
    const res = await updateList(id, { description });
    if (!res.success) toast.error(res.error || "Could not save the description.");
  };
  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    const res = await deleteList(target.id);
    if (res.success) { radarToast("List deleted · players moved to My Radar"); if (isMobile) setMobileView("home"); }
    else toast.error(res.error || "Could not delete the list.");
  };
  const handleSaveSharing = async (payload) => {
    const target = manageSharing;
    const res = await setSharing(target.id, payload);
    if (res.success) { radarToast("Sharing updated"); setManageSharing(null); }
    else toast.error(res.error || "Could not update sharing.");
  };
  const handleSetStatus = async (entry, status) => {
    const res = await setStatus(entry.id, status);
    if (!res.success) toast.error(res.error || "Could not set the status.");
  };
  const handleCopy = async (entry, list) => {
    const res = await copyEntry(entry.id, list.id);
    if (res.success) radarToast(`Copied to "${list.name}"`);
    else toast.error(res.error || "Could not copy the player.");
  };
  const handleMove = async (entry, list) => {
    const res = await moveEntry(entry.id, list.id);
    if (res.success) radarToast(`Moved to "${list.name}"`);
    else toast.error(res.error || "Could not move the player.");
  };
  const handleRemove = async (entry) => {
    if (detailEntry?.id === entry.id) setDetailEntry(null);
    const res = await removeEntry(entry.id);
    if (res.success) radarToast("Removed from this list");
    else toast.error(res.error || "Could not remove the player.");
  };
  // List-level bulk move/copy (⋯ "Move/Copy players to…") over the visible entries.
  const handleBulk = async (kind, targetList) => {
    const list = visibleEntries;
    let ok = 0;
    for (const e of list) {
      const res = kind === "move" ? await moveEntry(e.id, targetList.id) : await copyEntry(e.id, targetList.id);
      if (res.success) ok += 1;
    }
    if (ok) radarToast(`${kind === "move" ? "Moved" : "Copied"} ${ok} player${ok === 1 ? "" : "s"} to "${targetList.name}"`);
  };
  const handleContactRecommender = async (entry) => {
    const res = await clubRadarService.contactRecommender(entry.id);
    if (res.success && res.data?.channel_id) navigate(`/inbox?channel=${res.data.channel_id}`);
    else toast.error(res.error || "Could not open the chat.");
  };
  const handleOpenProfile = (entry) => {
    // Coach entries (Coach-Radar Phase 2) route to the coach profile.
    const coachId = entry.player?.ts_coach_id || entry.ts_coach_id;
    if (coachId) {
      navigate(`/search/coach/${coachId}`);
      return;
    }
    const pid = entry.player?.ws_player_id || entry.ws_player_id || entry.player?.player_id || entry.player_id;
    if (pid) navigate(`/search/player/${pid}`);
  };
  const handleShareList = () => {
    if (!selectedMeta) return;
    const players = entries.map((e) => {
      const p = e.player || e;
      return { name: p.name, initials: p.initials, position: p.position, club_name: p.club_name, image_url: p.image_url };
    });
    setShareTarget({ listId: selectedMeta.id, name: selectedMeta.name, count: selectedMeta.entry_count ?? entries.length, players });
  };

  if (!hasAccess) return <NoAccess />;

  const detailNode = detailEntry && (
    <ClubEntryDetail
      entry={detailEntry}
      mobile={isMobile}
      allLists={allLists}
      clubLabels={labels}
      clubName={clubName}
      currentUser={user}
      onClose={() => setDetailEntry(null)}
      onOpenProfile={handleOpenProfile}
      onSetStatus={setStatus}
      onAssignLabel={assignLabel}
      onUnassignLabel={unassignLabel}
      onCreateLabel={createLabel}
      onAddNote={addNote}
      onCopyToList={handleCopy}
      onContactRecommender={handleContactRecommender}
    />
  );

  const overlays = (
    <>
      {detailNode}
      {deleteTarget && (
        <ClubDeleteListModal mobile={isMobile} listName={deleteTarget.name} count={deleteTarget.entry_count ?? visibleEntries.length} onCancel={() => setDeleteTarget(null)} onConfirm={handleConfirmDelete} />
      )}
      {manageSharing && (
        <ClubManageSharingSheet list={manageSharing} clubName={clubName} mobile={isMobile} onClose={() => setManageSharing(null)} onSave={handleSaveSharing} />
      )}
      <ClubRadarShareAdapter target={shareTarget} clubName={clubName} sharedBy={user?.name} onClose={() => setShareTarget(null)} />
    </>
  );

  // Table footer caption (1a personal / 1b shared wording), real counts.
  const footerText = selectedMeta && (
    `${playersCountText(visibleEntries.length)} ${GLYPH.middot} ${
      (selectedMeta.share_tier || "personal") !== "personal"
        ? "anyone the list is shared with sees statuses, labels and notes"
        : "statuses and notes are visible to colleagues per their role"
    }`
  );

  // ── mobile ───────────────────────────────────────────────────────────────────
  if (isMobile) {
    // Lists home (frames 1d / 1f)
    if (mobileView === "home") {
      const q = mobileListQuery.trim().toLowerCase();
      const matches = (l) => !q || String(l.name || "").toLowerCase().includes(q);
      const homeMy = myLists.filter(matches);
      const homeShared = sharedLists.filter(matches);
      const Section = ({ label, lists, showEmpty }) => (lists.length > 0 || showEmpty) && (
        <>
          <Eyebrow style={{ margin: "16px 16px 6px" }}>{label}</Eyebrow>
          <div style={{ margin: "0 16px", background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
            {lists.length === 0 && (
              <div style={{ padding: "13px 14px", fontSize: 11.5, color: "#9CA3AF" }}>No lists</div>
            )}
            {lists.map((l, i) => (
              <button
                key={l.id}
                type="button"
                onClick={() => { setSelectedList(l.id); setMobileView("detail"); }}
                className="w-full text-left cursor-pointer"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", background: "none", border: "none", borderBottom: i === lists.length - 1 ? "none" : "1px solid #F1F5F9", fontFamily: "inherit" }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                    {l.share_tier && l.share_tier !== "personal" && <SharedChip>Shared</SharedChip>}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 2 }}>
                    {playersCountText(l.entry_count ?? 0)}{l.share_tier && l.share_tier !== "personal" ? ` ${GLYPH.middot} ${l.created_by ? `created by ${l.created_by.is_me ? "you" : l.created_by.name}` : "shared"}` : ` ${GLYPH.middot} personal`}
                  </div>
                </div>
                <span style={{ color: "#CBD5E1", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{GLYPH.chevronRight}</span>
              </button>
            ))}
          </div>
        </>
      );
      return (
        <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: FONT_FAMILY, display: "flex", flexDirection: "column" }}>
          <RadarTopBar mobile user={user} />
          {radarEmpty ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RadarEmpty mobile />
            </div>
          ) : (
            <>
              <SearchInput
                value={mobileListQuery}
                onChange={(e) => setMobileListQuery(e.target.value)}
                placeholder="Search radar"
                height={38}
                bg="#FFFFFF"
                style={{ margin: "14px 16px 0", padding: "0 12px" }}
              />
              <Section label="My lists" lists={homeMy} showEmpty />
              <Section label={`Shared with ${clubName}`} lists={homeShared} />
              <div style={{ flex: 1 }} />
              <DashedCtaButton height={44} radius={12} fontSize={13.5} style={{ width: "auto", margin: "14px 16px 18px" }} onClick={() => setMobileSheet("newlist")}>
                {GLYPH.plus} New list
              </DashedCtaButton>
            </>
          )}
          {mobileSheet === "newlist" && <MobileNewListSheet onCreate={handleCreateList} onClose={() => setMobileSheet(null)} />}
          {overlays}
        </div>
      );
    }
    // List detail (frame 1e)
    const createdWho = selectedMeta?.created_by ? (selectedMeta.created_by.is_me ? "you" : selectedMeta.created_by.name) : null;
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: FONT_FAMILY, display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#FFFFFF", borderBottom: "1px solid #E5E7EB", padding: "14px 16px 12px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <IconSquareButton size={30} radius={9} color="#374151" fontSize={16} onClick={() => setMobileView("home")} aria-label="Back" className="cursor-pointer">
              {GLYPH.chevronLeft}
            </IconSquareButton>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.2px", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedMeta?.name || "My Radar"}
                </span>
                {selectedMeta && <MobileTierPill list={selectedMeta} />}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>
                {playersCountText(visibleEntries.length)}{createdWho ? ` ${GLYPH.middot} created by ${createdWho}` : ""}
              </div>
              {selectedMeta?.description && (
                <div style={{ fontSize: 10.5, color: "#6B7280", fontStyle: "italic", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {GLYPH.quoteOpen}{selectedMeta.description}{GLYPH.quoteClose}
                </div>
              )}
            </div>
            {visibleEntries.length > 0 && (
              <TextButton height={30} fontSize={13} color="#2283FF" onClick={handleShareList} className="cursor-pointer">
                Share
              </TextButton>
            )}
            <IconSquareButton size={30} radius={9} onClick={() => setMobileSheet("listmenu")} aria-label="List actions" className="cursor-pointer">
              {GLYPH.ellipsisMenu}
            </IconSquareButton>
          </div>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
            <ClubFilterChips mobile filter={filter} setFilter={setFilter} counts={statusCounts} labels={labels} filterLabel={filterLabel} setFilterLabel={setFilterLabel} />
            <FilterChip mobile onClick={() => setMobileSheet("sort")} aria-label="Sort">
              Sort <span style={{ color: "#9CA3AF", fontSize: 10, marginLeft: 4 }}>{GLYPH.caretDown}</span>
            </FilterChip>
          </div>
        </div>

        {error && <div className="text-center text-[12.5px] text-[#DC2626]" style={{ padding: "20px 16px" }}>{error}</div>}
        {!loading && visibleEntries.length === 0 && !error && (
          radarFiltersActive ? (
            <div className="text-center text-[12.5px] text-[#9CA3AF]" style={{ padding: "36px 16px" }}>No players match.</div>
          ) : (
            <div className="text-center" style={{ padding: "36px 22px" }}>
              <div className="text-[14.5px] font-bold text-[#111827]">{EMPTY_LIST_TITLE}</div>
              <div className="text-[12.5px] text-[#6B7280] leading-[1.55]" style={{ maxWidth: 340, margin: "6px auto 0" }}>{EMPTY_LIST_BODY}</div>
            </div>
          )
        )}
        {visibleEntries.length > 0 && (
          <div style={{ background: "#FFFFFF" }}>
            {visibleEntries.map((entry) => {
              const e = { ...entry, _listId: selectedList };
              const note = entry.latest_note;
              const origin = entry.origin || {};
              // R5 swipe accelerators (artboard 1e revealed row): same handlers
              // as the row ⋯ sheet, same gates — Move/Remove need canManage
              // (F17/F18), Copy is open to any member (F16). Remove fires the
              // existing immediate handleRemove flow (F18: optimistic + toast,
              // no confirm dialog in the platform flow); Move/Copy open the
              // existing sheet directly on its list picker. The ⋯ stays on
              // every row as the gesture-free path.
              const swipeActions = canManage
                ? [
                    { key: "move", label: "Move", ariaLabel: "Move to list", onAction: () => { setEntryActionsView("move"); setEntryActions(e); } },
                    { key: "remove", label: "Remove", ariaLabel: "Remove from list", destructive: true, onAction: () => handleRemove(e) },
                  ]
                : [
                    { key: "copy", label: "Copy", ariaLabel: "Copy to list", onAction: () => { setEntryActionsView("copy"); setEntryActions(e); } },
                  ];
              return (
                <SwipeActionRow
                  key={entry.id}
                  actions={swipeActions}
                  style={{ borderBottom: "1px solid #F1F5F9" }}
                >
                <div
                  onClick={() => setDetailEntry(entry)}
                  className="cursor-pointer"
                  style={{ padding: "12px 16px", background: "#FFFFFF" }}
                >
                  <ClubPlayerCell
                    player={entry.player || entry}
                    mobile
                    trailing={
                      <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                        <StatusPill mobile status={entry.status} label={clubStatusMeta(entry.status).label} />
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); setEntryActionsView(null); setEntryActions(e); }}
                          aria-label="Actions"
                          className="cursor-pointer"
                          style={{ background: "none", border: "none", color: "#9CA3AF", fontSize: 15, fontWeight: 700, padding: "0 2px", fontFamily: "inherit" }}
                        >
                          {GLYPH.ellipsisMenu}
                        </button>
                      </span>
                    }
                  />
                  {note?.text && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {GLYPH.quoteOpen}{note.text}{GLYPH.quoteClose} {GLYPH.emDash} {note.author_name}
                    </div>
                  )}
                  {origin.top && <SourceLines mobile top={origin.top} sub={origin.sub} style={{ marginTop: 4 }} />}
                </div>
                </SwipeActionRow>
              );
            })}
          </div>
        )}
        {visibleEntries.length > 0 && (
          <div style={{ padding: "12px 16px", fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>
            Swipe a row for quick actions {GLYPH.middot} statuses visible to colleagues
          </div>
        )}

        {mobileSheet === "sort" && (
          <MobileSortSheet sortBy={sortBy} setSortBy={setSortBy} onClose={() => setMobileSheet(null)} />
        )}
        {mobileSheet === "listmenu" && selectedMeta && (
          <MobileListMenuSheet
            list={selectedMeta}
            canManage={canManage}
            onRename={() => { const n = window.prompt(tr("Rename list"), selectedMeta.name); if (n && n.trim()) handleRename(selectedMeta.id, n.trim()); }}
            onEditDescription={() => { const d = window.prompt(tr("List description"), selectedMeta.description || ""); if (d != null) handleSaveDescription(selectedMeta.id, d.trim()); }}
            onManageSharing={() => setManageSharing(selectedMeta)}
            onShareToChat={handleShareList}
            onDelete={() => setDeleteTarget(selectedMeta)}
            onClose={() => setMobileSheet(null)}
          />
        )}
        {entryActions && (
          <MobileEntryActionsSheet entry={entryActions} lists={allLists} canManage={canManage} initialView={entryActionsView} onOpen={(en) => setDetailEntry(en)} onSetStatus={handleSetStatus} onCopy={handleCopy} onMove={handleMove} onRemove={handleRemove} onClose={() => { setEntryActions(null); setEntryActionsView(null); }} />
        )}
        {mobileSheet === "newlist" && <MobileNewListSheet onCreate={handleCreateList} onClose={() => setMobileSheet(null)} />}
        {overlays}
      </div>
    );
  }

  // ── desktop (frames 1a/1b/1c) ────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: FONT_FAMILY }}>
      <div style={{ maxWidth: 1920, width: "100%", margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <RadarTopBar user={user} />
        <div style={{ flex: 1, display: "flex", alignItems: "stretch", minHeight: 0 }}>
          <ClubListsSidebar myLists={myLists} sharedLists={sharedLists} selectedList={selectedList} onSelect={setSelectedList} onNewList={() => setNewListOpen(true)} clubName={clubName} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {radarEmpty ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <RadarEmpty />
              </div>
            ) : (
              <>
                <div style={{ padding: "18px 24px 14px" }}>
                  {selectedMeta && (
                    <ClubListHeader
                      list={selectedMeta}
                      count={visibleEntries.length}
                      allLists={allLists}
                      canManage={canManage}
                      sortBy={sortBy}
                      onSortChange={setSortBy}
                      onShareToChat={handleShareList}
                      onRename={handleRename}
                      onSaveDescription={handleSaveDescription}
                      onManageSharing={() => setManageSharing(selectedMeta)}
                      onBulkMove={(l) => handleBulk("move", l)}
                      onBulkCopy={(l) => handleBulk("copy", l)}
                      onDelete={() => setDeleteTarget(selectedMeta)}
                    />
                  )}
                  <ClubFilterChips filter={filter} setFilter={setFilter} counts={statusCounts} labels={labels} filterLabel={filterLabel} setFilterLabel={setFilterLabel} style={{ marginTop: 12 }} />
                </div>
                <div style={{ margin: "0 24px 24px" }}>
                  {error && <div className="text-center text-[12.5px] text-[#DC2626]" style={{ padding: "40px 0" }}>{error}</div>}
                  {!error && !loading && visibleEntries.length === 0 && (
                    radarFiltersActive ? (
                      <div className="text-center text-[12.5px] text-[#9CA3AF]" style={{ padding: "40px 0", background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 16 }}>No players match your filters.</div>
                    ) : (
                      <div className="text-center" style={{ padding: "48px 24px", background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 16 }}>
                        <div className="text-[14.5px] font-bold text-[#111827]">{EMPTY_LIST_TITLE}</div>
                        <div className="text-[12.5px] text-[#6B7280] leading-[1.55]" style={{ maxWidth: 340, margin: "7px auto 0" }}>{EMPTY_LIST_BODY}</div>
                      </div>
                    )
                  )}
                  {visibleEntries.length > 0 && (
                    <ClubEntryTable entries={visibleEntries} lists={allLists} canManage={canManage} selectedList={selectedList} onOpen={(e) => setDetailEntry(e)} onSetStatus={handleSetStatus} onCopy={handleCopy} onMove={handleMove} onRemove={handleRemove} footer={footerText} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {newListOpen && <NewListModal onCreate={handleCreateList} onClose={() => setNewListOpen(false)} />}
      {overlays}
    </div>
  );
};

export default ClubRadar;
